import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, MapPin } from 'lucide-react';
import Marquee from '../components/Marquee';
import PromoPopup from '../components/PromoPopup';
import SectionEyebrow from '../components/SectionEyebrow';
import ReservationModal from '../components/ReservationModal';
import { usePage, useSite, img } from '../lib/content';
import SmartMedia from '../components/SmartMedia';
import Seo from '../components/Seo';

const Home = () => {
  const c = usePage('home');
  const site = useSite();
  const [reserveOpen, setReserveOpen] = useState(false);
  const experiences = Array.isArray(c.experiences) ? c.experiences : [];
  const weekly = Array.isArray(c.weekly) ? c.weekly : [];

  return (
    <div className="bg-[#f7f1e3]">
      <Seo title="Millpond Gatherings · Steakhouse, Tavern & Event Venue in Northford, CT" description="A steakhouse, a tavern, and a wedding venue set in a historic 1700s mill on the river in Northford, Connecticut. Reserve a table or plan your event." path="/" />
      <ReservationModal open={reserveOpen} onClose={() => setReserveOpen(false)} />
      <PromoPopup promos={c.promoPopups} />
      {/* HERO */}
      <section className="relative h-[100vh] min-h-[720px] w-full overflow-hidden">
        <SmartMedia src={c.heroImage} alt="Millpond exterior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#f7f1e3] via-transparent to-transparent" style={{ backgroundSize: '100% 35%', backgroundRepeat: 'no-repeat', backgroundPosition: 'bottom' }} />

        <div className="hidden md:flex absolute top-32 right-10 lg:right-16 flex-col items-end text-[#f7f1e3] gap-2">
          <p className="text-[11px] tracking-[0.28em] uppercase opacity-80">{c.visitLabel}</p>
          <div className="flex items-center gap-2 font-serif text-lg">
            <MapPin size={16} className="opacity-80" />
            {site.address}
          </div>
          <a href={`tel:${site.phoneRaw}`} className="font-serif text-lg opacity-90 hover:opacity-100">
            {site.phone}
          </a>
        </div>

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-32">
          <SectionEyebrow light>{c.heroEyebrow}</SectionEyebrow>
          <h1 className="font-serif text-[#f7f1e3] text-[56px] leading-[1.02] md:text-[88px] lg:text-[112px] tracking-[-0.02em] mt-6 max-w-5xl">
            {c.heroTitle}
          </h1>
          <p className="text-[#f1ead9]/90 mt-6 max-w-xl text-base md:text-lg leading-relaxed">
            {c.heroBody}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            {(Array.isArray(c.heroButtons) ? c.heroButtons : []).filter((b) => b && b.label).map((b, i) => {
              const href = b.fileUrl ? img(b.fileUrl) : b.url;
              const cls = i === 0
                ? "px-8 py-4 bg-[#942222] hover:bg-[#b13030] text-[#f1ead9] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors"
                : "px-8 py-4 border border-[#f1ead9] text-[#f1ead9] hover:bg-[#f1ead9] hover:text-[#942222] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors";
              return (
                <a key={i} href={href || '#'} target="_blank" rel="noreferrer" data-testid={`home-hero-button-${i}`} className={`${cls} text-center leading-tight`}>
                  <span className="block">{b.label}</span>
                  {b.subLabel && <span className="block text-[9px] tracking-[0.18em] opacity-75 mt-1 normal-case font-normal">{b.subLabel}</span>}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* THREE EXPERIENCES */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <SectionEyebrow>{c.experiencesEyebrow}</SectionEyebrow>
              <h2 className="font-serif text-[#942222] text-[44px] md:text-[60px] lg:text-[72px] leading-[1.05] tracking-[-0.02em] mt-5">
                {c.experiencesTitle}
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#942222]/80 text-base lg:text-[17px] leading-relaxed lg:pl-8 lg:border-l lg:border-[#942222]/15">
                {c.experiencesBody}
              </p>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {experiences.map((e) => (
              <Link to={e.to} key={e.id || e.title} className="group block">
                <div className="relative aspect-[3/4] overflow-hidden">
                  <SmartMedia src={e.image} alt={e.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                  <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                    <p className="text-[#f1ead9] text-[10px] tracking-[0.28em] uppercase opacity-90">{e.eyebrow}</p>
                    <ArrowUpRight size={20} className="text-[#f1ead9] opacity-80 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="font-serif text-[#f7f1e3] text-4xl md:text-5xl tracking-tight">{e.title}</h3>
                  </div>
                </div>
                <p className="mt-4 text-[15px] text-[#942222]/80">{e.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HERITAGE */}
      <section className="bg-[#942222] text-[#f1ead9] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <SectionEyebrow light>{c.heritageEyebrow}</SectionEyebrow>
            <h2 className="font-serif text-[44px] md:text-[58px] lg:text-[68px] leading-[1.05] tracking-[-0.02em] mt-5">
              {c.heritageTitle}
            </h2>
            <div className="mt-8 space-y-5 text-[#f1ead9]/85 text-[16px] leading-relaxed max-w-xl">
              <p>{c.heritageBody1}</p>
              <p>{c.heritageBody2}</p>
            </div>
            <Link to="/contact" className="inline-block mt-10 px-8 py-4 bg-[#e2b95a] hover:bg-[#cea444] text-[#942222] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors">
              {c.heritageCtaLabel}
            </Link>
          </div>
          <div className="relative aspect-[4/5] overflow-hidden">
            <SmartMedia src={c.heritageImage} alt="Banquet hall" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* WEEKLY RHYTHM */}
      <section className="py-24 lg:py-32 bg-[#f7f1e3]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col items-start">
            <SectionEyebrow>{c.weeklyEyebrow}</SectionEyebrow>
            <h2 className="font-serif text-[#942222] text-[44px] md:text-[60px] lg:text-[72px] leading-[1.05] tracking-[-0.02em] mt-5 max-w-3xl">
              {c.weeklyTitle}
            </h2>
          </div>

          <div className="mt-16 divide-y divide-[#942222]/15 border-t border-b border-[#942222]/15">
            {weekly.map((w, i) => (
              <div key={i} className="grid grid-cols-12 gap-6 py-7 lg:py-9 items-baseline group hover:bg-[#efe6d0] transition-colors px-2 lg:px-4 -mx-2 lg:-mx-4">
                <div className="col-span-12 md:col-span-3 lg:col-span-3 text-[11px] tracking-[0.28em] uppercase text-[#942222]/60">
                  {w.day}
                </div>
                <div className="col-span-12 md:col-span-4 lg:col-span-4">
                  <h3 className="font-serif text-[#942222] text-3xl md:text-4xl tracking-tight group-hover:text-[#b13030] transition-colors">
                    {w.title}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-5 lg:col-span-5">
                  <p className="text-[#942222]/80 text-[15px] lg:text-base leading-relaxed">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 lg:py-40 bg-[#f7f1e3] text-center">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-[#e2b95a] flex items-center justify-center">
              <svg width="42" height="42" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <g fill="none" stroke="#942222" strokeWidth="4" strokeLinecap="square">
                  <path d="M28 28 L28 64" /><path d="M28 28 L50 56" /><path d="M72 28 L50 56" /><path d="M72 28 L72 64" />
                </g>
              </svg>
            </div>
          </div>
          <h2 className="mt-10 font-serif text-[#942222] text-[44px] md:text-[60px] lg:text-[80px] leading-[1.04] tracking-[-0.02em] max-w-4xl mx-auto">
            {c.ctaTitle}
          </h2>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <button type="button" onClick={() => setReserveOpen(true)} data-testid="home-cta-reserve-button" className="px-8 py-4 bg-[#942222] hover:bg-[#b13030] text-[#f1ead9] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors">
              {c.ctaReserveLabel}
            </button>
            <Link to="/venue" className="px-8 py-4 border border-[#942222] text-[#942222] hover:bg-[#942222] hover:text-[#f1ead9] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors">
              {c.ctaEventLabel}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
